// layout/Sidebar.js
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/auth-context';
import globalMenuConfig from '../menu/menuConfig';
import { readScope as readScopedStorage } from '../state/scopeStorage';
import '../menu/menu.css';

const LS_GROUPS   = 'ui.menu.expandedGroups';
const LS_SECTIONS = 'ui.menu.expandedSections';

// Read active scope (org/dataset) via helper
function useActiveScope() {
  const calc = () => {
    try {
      const s = readScopedStorage();
      return {
        orgId: s?.org?.id || null,
        orgName: s?.org?.name || null,
        datasetId: s?.dataset?.id || null,
        datasetName: s?.dataset?.name || null,
      };
    } catch {
      return { orgId: null, orgName: null, datasetId: null, datasetName: null };
    }
  };
  const [state, setState] = useState(calc);




  useEffect(() => {
    const onStorage = (e) => {
      if (!e) return;
      if (
        [
          'active-scope',
          'activeOrgId',
          'activeOrg',
          'activeOrgName',
          'activeOrgLabel',
          'activeDataSetId',
          'activeDataSetName',
          'datasetId',
          'datasetName',
        ].includes(e.key)
      ) {
        setState(calc());
      }
    };
    const onCustom = () => setState(calc());
    window.addEventListener('storage', onStorage);
    window.addEventListener('scope:changed', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('scope:changed', onCustom);
    };
  }, []);

  return state;
}

function loadJSON(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

function hasAccess(
  item,
  user
) {

  // No roles specified
  if (
    !item.roles ||
    item.roles.length === 0
  ) {
    return true;
  }

  // superadmin override
  // superadmin override
  if (
    user?.globals?.includes('superadmin')
  ) {
    return true;
  }

  const userRoles =
    user?.roles || [];

  return item.roles.some(
    role =>
      userRoles.includes(
        role
      )
  );

}


export default function Sidebar({ isOpen, menuConfig = globalMenuConfig, closeSidebar }) {
  const { user } = useAuth();
  const { orgId, datasetId } = useActiveScope();
  const loc = useLocation();
  console.log("user:", user)


  // Groups open by default; rehydrate from LS
  const [expandedGroups, setExpandedGroups] = useState(() =>
    loadJSON(LS_GROUPS, { Studio: true, Applications: true, Production: true })
  );

  // Per-section expand/collapse (keyed "GroupLabel/SectionLabel"); rehydrate from LS
  const [expandedSections, setExpandedSections] = useState(() =>
    loadJSON(LS_SECTIONS, { 'Studio/Data': true })
  );

  // persist changes
  useEffect(() => {
    try { localStorage.setItem(LS_GROUPS, JSON.stringify(expandedGroups)); } catch {}
  }, [expandedGroups]);
  useEffect(() => {
    try { localStorage.setItem(LS_SECTIONS, JSON.stringify(expandedSections)); } catch {}
  }, [expandedSections]);

  // optional: close on mobile after navigation
  useEffect(() => {
    if (window.innerWidth <= 768 && isOpen) closeSidebar?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  if (!user) return null;

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const toggleSection = (parentLabel, sectionLabel) => {
    const key = `${parentLabel}/${sectionLabel}`;
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isSectionOpen = (parentLabel, sectionLabel) =>
    !!expandedSections[`${parentLabel}/${sectionLabel}`];

  const resolvePath = (path) => {
    if (!path) return path;
    if (datasetId && path.includes(':datasetId')) {
      return path.replace(':datasetId', encodeURIComponent(datasetId));
    }
    return path;
  };

  const isDisabled = (item) => {
    if (item.requiresDataSet && !datasetId) return true;
    if (item.requiresOrg && !orgId) return true;
    return false;
  };

  const onItemClick = () => {
    if (window.innerWidth <= 768) closeSidebar?.();
  };

  const renderChildren = (children, parentLabel) => {
  return (children || [])
    .filter(child =>
      hasAccess(
        child,
        user
      )
    )
    .map((child, idx) => {
      // Collapsible subsection
      if (child.type === 'section' && Array.isArray(child.children)) {
        const open = isSectionOpen(parentLabel, child.label);
        return (
          <li key={`sec-${parentLabel}-${child.label}-${idx}`} className="menu-subsection">
            <button
              className="menu-subsection-toggle"
              onClick={() => toggleSection(parentLabel, child.label)}
              aria-expanded={open ? 'true' : 'false'}
            >
              <span className="chev">{open ? '▾' : '▸'}</span>
              <span className="menu-subsection-title">{child.label}</span>
            </button>
            {open && (
              <ul className="menu-subsection-list">
                {child.children.map((leaf, jdx) => {
                  const disabled = isDisabled(leaf);
                  const finalPath = resolvePath(leaf.path);
                  if (disabled) {
                    return (
                      <li
                        key={`sec-leaf-${parentLabel}-${child.label}-${jdx}`}
                        title="Select the required context in the header (organization or dataset)"
                      >
                        <span style={{ color: '#9ca3af', cursor: 'not-allowed' }}>{leaf.label}</span>
                      </li>
                    );
                  }
                  return (
                    <li key={`sec-leaf-${parentLabel}-${child.label}-${jdx}`}>
                      <Link to={finalPath} onClick={onItemClick}>
                        {leaf.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      }

      // Regular leaf item
      const disabled = isDisabled(child);
      const finalPath = resolvePath(child.path);
      if (disabled) {
        return (
          <li
            key={`grp-item-${parentLabel}-${idx}`}
            title="Select the required context in the header (organization or dataset)"
          >
            <span style={{ color: '#9ca3af', cursor: 'not-allowed' }}>{child.label}</span>
          </li>
        );
      }
      return (
        <li key={`grp-item-${parentLabel}-${idx}`}>
          <Link to={finalPath} onClick={onItemClick}>
            {child.label}
          </Link>
        </li>
      );
    });
  };

  return (
    <div
      className={`sidebar ${isOpen ? 'open' : 'closed'}`}

    >
      <nav>
        <ul>
        {menuConfig.map((item, index) => {

          if (
            !hasAccess(
              item,
              user
            )
          ) {
            return null;
          }

          if (
            item.type === 'groupSingle'
          ) {

              const finalPath =
                resolvePath(item.path);

              return (

                <li key={`grp-${item.label}`}>

                  {finalPath ? (

                    <Link
                      to={finalPath}
                      className="menu-group-link-single"
                      onClick={onItemClick}
                    >
                      {item.label}
                    </Link>

                  ) : (

                    <div className="menu-group-link-single">
                      {item.label}
                    </div>

                  )}

                </li>

              );
            }

            if (item.type === 'group') {

              const visibleChildren =
                (item.children || [])
                  .filter(child =>
                    hasAccess(
                      child,
                      user
                    )
                  );

              // Hide group entirely if none
              // of its children are visible
              if (
                item.children &&
                visibleChildren.length === 0
              ) {
                return null;
              }

              const groupOpen =
                !!expandedGroups[item.label];

              const finalPath =
                resolvePath(item.path);

              return (
                <li key={`grp-${item.label}`}>
                <div className={ finalPath
                      ? 'menu-group-row menu-group-row-button'
                      : 'menu-group-row'}
                >
                    {/* CLICKABLE LABEL */}

                    {finalPath ? (

                      <Link
                        to={finalPath}
                        className="menu-group-link"
                        onClick={onItemClick}
                      >
                        {item.label}
                      </Link>

                    ) : (

                      <div className="menu-group-link">
                        {item.label}
                      </div>

                    )}

                    {/* SEPARATE EXPAND BUTTON */}

                    <button
                      className="menu-group-toggle"
                      onClick={() =>
                        toggleGroup(item.label)
                      }
                    >
                      {groupOpen ? '▲' : '▼'}
                    </button>

                  </div>

                  {groupOpen && (
                    <ul className="menu-sublist">

                      {renderChildren(
                        visibleChildren,
                        item.label
                      )}

                    </ul>
                  )}

                </li>
              );
            }

            // top-level item
            const disabled = isDisabled(item);
            const finalPath = resolvePath(item.path);
            if (disabled) {
              return (
                <li
                  key={`itm-${item.label}`}
                  title="Select the required context in the header (organization or dataset)"
                >
                  <span style={{ color: '#9ca3af', cursor: 'not-allowed' }}>{item.label}</span>
                </li>
              );
            }
            let highlight = "normal";
            if (item.label === "Organizations")
              highlight = "bold";
            return (
              <li key={`itm-${item.label}`}>
                <Link to={finalPath} onClick={onItemClick} style={{fontWeight: highlight}}>
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
